import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/** Space kept above a #fragment target when it sets no scroll-margin-top (the fixed header). */
export const HASH_OFFSET_PX = 96;
/** How long to wait for a lazily rendered #fragment target: 20 tries, 100ms apart. */
export const HASH_RETRY_MS = 100;
export const HASH_MAX_TRIES = 20;

/** Back / Forward: where each history entry was scrolled (memory, plus sessionStorage for reloads). */
export const SCROLL_POSITIONS_KEY = 'tappd_scroll_positions';
/** How long a Back / Forward waits for the page to be tall enough: 40 tries, 50ms apart. */
export const RESTORE_RETRY_MS = 50;
export const RESTORE_MAX_TRIES = 40;
const MAX_POSITIONS = 50;
const memoryPositions = new Map();

/** After a new page: how long to wait for its <main> (a lazy page shows PageLoading first). */
export const MAIN_RETRY_MS = 100;
export const MAIN_MAX_TRIES = 20;
/** The canvas's content wrapper (layouts/PublicLayout SITE_CONTENT_ID): the fallback target. */
const CONTENT_ID = 'site-content';

/** The page's <main> (inside the site canvas when there is one), else the content wrapper. */
function pageMain() {
  const root = document.getElementById(CONTENT_ID);
  return (root && root.querySelector('main')) || document.querySelector('main') || root || null;
}

/**
 * Focus a landmark without scrolling: tabindex="-1" while it holds focus (removed on blur;
 * an element with its own tabindex keeps it). index.css draws no ring on a focused main.
 */
function focusLandmark(el) {
  if (!el.hasAttribute('tabindex')) {
    el.setAttribute('tabindex', '-1');
    el.addEventListener('blur', () => el.removeAttribute('tabindex'), { once: true });
  }
  el.focus({ preventScroll: true });
}

/** A history entry's id: its router key plus the URL (initial entries all share the key "default"). */
export const scrollEntryId = ({ key, pathname, search }) => `${key || 'default'}|${pathname || ''}${search || ''}`;

function readStoredPositions() {
  try {
    const all = JSON.parse(window.sessionStorage.getItem(SCROLL_POSITIONS_KEY) || '{}');
    return all && typeof all === 'object' && !Array.isArray(all) ? all : {};
  } catch (e) {
    return null;
  }
}

/** The saved scroll position of a history entry, or null. */
export function savedScrollPosition(id) {
  if (memoryPositions.has(id)) return memoryPositions.get(id);
  const all = readStoredPositions();
  const y = all ? all[id] : undefined;
  return typeof y === 'number' && y >= 0 ? y : null;
}

/** Remember where a history entry was scrolled (the newest MAX_POSITIONS are kept). */
export function saveScrollPosition(id, y) {
  if (!id || typeof y !== 'number' || !(y >= 0)) return;
  memoryPositions.delete(id);
  memoryPositions.set(id, y);
  if (memoryPositions.size > MAX_POSITIONS) memoryPositions.delete(memoryPositions.keys().next().value);
  const all = readStoredPositions();
  if (!all) return;
  delete all[id];
  all[id] = y;
  const kept = Object.entries(all).slice(-MAX_POSITIONS);
  try {
    window.sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch (e) {
    // storage full or blocked: memory still has it
  }
}

const reducedMotion = () => {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};

const targetFor = (hash) => {
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch (e) {
    // keep the raw id
  }
  return id ? document.getElementById(id) : null;
};

/**
 * Scroll position and focus on navigation (mounted once in App.js):
 * - Back / Forward (a POP after the first load): back to where that history entry was
 *   scrolled, once the page is tall enough (retried for up to 2s for content that renders
 *   late; given up if the visitor scrolls first). Positions are recorded from scroll events
 *   per history entry (key + URL). Without a saved position it leaves the scroll alone —
 *   never to the top. (A #fragment entry with no saved position still goes to its target.)
 * - a new path without a #fragment (PUSH or REPLACE, not the first load): to the top, and
 *   focus moves to the new page's <main> (tabindex -1, no scroll; the content wrapper
 *   #site-content when a page has no main) once it has rendered — waiting out a lazy page's
 *   loading state — so the next Tab starts in the page, not at the top of the document. Not
 *   when the page (or a dialog) already holds focus, and given up if the visitor types,
 *   clicks or scrolls first. Back / Forward and same-path changes (filters, search) never
 *   move focus;
 * - a location with a #fragment (e.g. "Get started" → /#choose, or a deep link): to that
 *   element once it has rendered — retried for up to 2s for lazily loaded pages, and given
 *   up if the visitor scrolls first. It lands below the fixed header: the target's own
 *   scroll-margin-top (e.g. ANCHOR_OFFSET) if it sets one, else HASH_OFFSET_PX. Smooth on
 *   the same page, instant on a new page and whenever reduced motion is requested. Focus
 *   moves to the target on in-app navigation (not on first load), so Tab continues there;
 *   a target that is not focusable gets tabindex="-1" for that.
 * - the same path without a fragment (search or state change): nothing, as before.
 */
function ScrollToTop() {
  const { pathname, hash, key, search } = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef(null);
  const shown = useRef(null); // the history entry on screen
  const lastY = useRef(typeof window !== 'undefined' ? window.scrollY || 0 : 0);

  // Where the entry on screen is scrolled (scroll events; saved again when the tab hides).
  useEffect(() => {
    const onScroll = () => {
      lastY.current = window.scrollY || window.pageYOffset || 0;
    };
    const onHide = () => {
      if (shown.current) saveScrollPosition(shown.current, lastY.current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onHide);
    };
  }, []);

  // Leaving an entry: keep its position. A layout effect, so a scroll event the new page
  // causes (a shorter page clamps the scroll) is already counted for the new entry.
  useLayoutEffect(() => {
    const id = scrollEntryId({ key, pathname, search });
    if (shown.current && shown.current !== id) saveScrollPosition(shown.current, lastY.current);
    shown.current = id;
  }, [key, pathname, search]);

  useEffect(() => {
    const first = previous.current === null;
    const samePath = previous.current === pathname;
    previous.current = pathname;
    const events = ['wheel', 'touchmove', 'keydown'];

    if (!first && navigationType === 'POP') {
      const y = savedScrollPosition(scrollEntryId({ key, pathname, search }));
      if (y !== null) {
        let tries = 0;
        let timer = null;
        let cancelled = false;
        const cancel = () => {
          cancelled = true;
          if (timer) clearTimeout(timer);
        };
        const attempt = () => {
          if (cancelled) return;
          tries += 1;
          const room = (document.documentElement.scrollHeight || 0) - (window.innerHeight || 0);
          if (room >= y - 1 || tries >= RESTORE_MAX_TRIES) {
            events.forEach((e) => window.removeEventListener(e, cancel));
            window.scrollTo(0, y);
            return;
          }
          timer = setTimeout(attempt, RESTORE_RETRY_MS);
        };
        events.forEach((e) => window.addEventListener(e, cancel, { passive: true, once: true }));
        timer = setTimeout(attempt, 0);
        return () => {
          cancel();
          events.forEach((e) => window.removeEventListener(e, cancel));
        };
      }
      // Back / Forward with no saved position: leave the scroll where it is.
      if (!hash || hash === '#') return undefined;
    } else if (!hash || hash === '#') {
      if (samePath) return undefined;
      window.scrollTo(0, 0);
      if (first) return undefined;
      // A new page: focus its <main> once it is there.
      let mainTries = 0;
      let mainTimer = null;
      let stopped = false;
      const interactions = ['keydown', 'pointerdown', 'wheel', 'touchmove'];
      const stop = () => {
        stopped = true;
        if (mainTimer) clearTimeout(mainTimer);
      };
      const place = () => {
        if (stopped) return;
        const el = pageMain();
        const loading = el && typeof el.matches === 'function' && el.matches('[data-testid="page-loading"]');
        if (!el || loading) {
          mainTries += 1;
          if (mainTries < MAIN_MAX_TRIES) mainTimer = setTimeout(place, MAIN_RETRY_MS);
          return;
        }
        interactions.forEach((e) => window.removeEventListener(e, stop, true));
        const active = document.activeElement;
        const busy = active && active !== document.body && active !== document.documentElement
          && (el.contains(active) || (typeof active.closest === 'function' && active.closest('[role="dialog"], dialog')));
        if (busy || active === el) return;
        focusLandmark(el);
      };
      interactions.forEach((e) => window.addEventListener(e, stop, { capture: true, passive: true, once: true }));
      mainTimer = setTimeout(place, 0);
      return () => {
        stop();
        interactions.forEach((e) => window.removeEventListener(e, stop, true));
      };
    } else if (!samePath) {
      window.scrollTo(0, 0);
    }
    let tries = 0;
    let timer = null;
    let cancelled = false;
    const cancel = () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };

    const attempt = () => {
      if (cancelled) return;
      const el = targetFor(hash);
      if (!el) {
        tries += 1;
        if (tries < HASH_MAX_TRIES) timer = setTimeout(attempt, HASH_RETRY_MS);
        return;
      }
      events.forEach((e) => window.removeEventListener(e, cancel));
      const margin = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
      const top = el.getBoundingClientRect().top + window.scrollY - (margin > 0 ? margin : HASH_OFFSET_PX);
      const smooth = samePath && !reducedMotion();
      window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
      if (!first) {
        if (!el.hasAttribute('tabindex') && !el.matches('a[href], button, input, select, textarea, summary')) {
          el.setAttribute('tabindex', '-1');
        }
        el.focus({ preventScroll: true });
      }
    };

    // The visitor scrolling (or typing) before the target turns up wins.
    events.forEach((e) => window.addEventListener(e, cancel, { passive: true, once: true }));
    timer = setTimeout(attempt, 0);
    return () => {
      cancel();
      events.forEach((e) => window.removeEventListener(e, cancel));
    };
  }, [pathname, hash, key, search, navigationType]);

  return null;
}

export default ScrollToTop;
